import { useId } from 'react';
import { effectivePaceLabel, effectivePlaybackPace, filmDuration, isTargetDuration, pacedFilmOptions,
  MIN_TARGET_DURATION_SECONDS, TARGET_DURATION_PRESETS, type FilmTiming } from '../domain/film-timing';
import { MAX_RENDER_DURATION_SECONDS, MIN_RENDER_DURATION_SECONDS, isPlaybackSecondsPerKilometer } from '../domain/playback-pace';
import { formatDuration } from '../domain/ride';
import { SocialVideoLimits } from './SocialVideoLimits';
import './playback-timing.css';
import type { UnitSystem } from '../domain/measurement-units';
import { CompactRangeControl } from './CompactRangeControl';

export interface PlaybackTimingControlProperties {
  readonly distanceKilometers: number;
  readonly timing: FilmTiming;
  readonly unitSystem?: UnitSystem;
  readonly hasExportRange?: boolean;
  readonly allowShortFilms?: boolean;
  readonly onChange: (timing: FilmTiming) => void;
}

export function PlaybackTimingControl({ distanceKilometers, timing, onChange, unitSystem = 'metric', hasExportRange = false, allowShortFilms = false }: PlaybackTimingControlProperties) {
  const helpId = useId();
  const duration = filmDuration(distanceKilometers, timing);
  const paceOptions = pacedFilmOptions(distanceKilometers);
  const selectedPace = paceOptions.find(option => option.durationSeconds === duration)?.pace ?? timing.pace;
  const minimum = allowShortFilms ? MIN_RENDER_DURATION_SECONDS : MIN_TARGET_DURATION_SECONDS;
  const setTarget = (seconds: number) => {
    if (isTargetDuration(seconds) || allowShortFilms && Number.isInteger(seconds) && seconds >= minimum && seconds <= MAX_RENDER_DURATION_SECONDS) onChange({ ...timing, targetSeconds: seconds });
  };
  return <div className="playback-timing">
    <label className="tool-control tool-places-toggle">
      <input type="checkbox" role="switch" checked={timing.mode === 'duration'}
        onChange={event => onChange({ ...timing, mode: event.target.checked ? 'duration' : 'pace' })} />
      <strong>Set video length</strong>
    </label>
    {timing.mode === 'duration' ? <div className="tool-control target-duration">
      <CompactRangeControl label="Video length (seconds)" value={timing.targetSeconds} min={minimum}
        max={MAX_RENDER_DURATION_SECONDS} unit="s" onChange={setTarget}
        invalidMessage={`Enter ${minimum}–${MAX_RENDER_DURATION_SECONDS} whole seconds. The last valid length is still selected.`} />
      <div className="duration-presets" aria-label="Video length shortcuts">
        {TARGET_DURATION_PRESETS.map(seconds => <button key={seconds} type="button"
          aria-pressed={timing.targetSeconds === seconds} onClick={() => setTarget(seconds)}>{seconds}s</button>)}
      </div>
      <small id={helpId}>{hasExportRange ? 'The IN/OUT footage plays for the selected video length. Opening and finish appear only when included.'
        : `Maximum ${MAX_RENDER_DURATION_SECONDS} seconds, including the 6-second opening and finish.`}</small>
      <small>60s: Instagram Stories · 140s: X standard · 180s: YouTube Shorts</small>
      <SocialVideoLimits />
    </div> : <label className="tool-control">
      <span>Playback pace</span>
      <select aria-label="Playback pace" value={selectedPace} onChange={event => {
        const pace = Number(event.target.value);
        if (isPlaybackSecondsPerKilometer(pace)) onChange({ ...timing, pace });
      }}>
        {paceOptions.map(option => <option key={option.pace} value={option.pace}>
          {effectivePaceLabel(option.effectivePace, unitSystem)} · {formatDuration(option.durationSeconds).slice(3)}
        </option>)}
      </select>
      <small>Maximum {MAX_RENDER_DURATION_SECONDS} seconds, including the 6-second opening and finish.</small>
    </label>}
    <p className="timing-summary" role="status">{hasExportRange && timing.mode === 'duration' ? <>Selected video length: {formatDuration(duration)}</>
      : <>{effectivePaceLabel(effectivePlaybackPace(distanceKilometers, duration), unitSystem)}<br />Film length: {formatDuration(duration)}</>}</p>
  </div>;
}
